import React from 'react';
import { useTranslation } from 'react-i18next';
import { getTeamColorClasses } from '../../../utils/teamColors';
import type { GameMode, TeamName } from '@business-game/shared';

interface FirstRoundModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamName?: TeamName | null;
  gameMode?: GameMode | null;
}

export const FirstRoundModal: React.FC<FirstRoundModalProps> = ({
  isOpen,
  onClose,
  teamName,
  gameMode,
}) => {
  const { t } = useTranslation();
  const teamColors = getTeamColorClasses(teamName || null, gameMode ?? null);

  if (!isOpen) return null;

  // Get gradient classes based on team color
  const getGradientClasses = () => {
    if (teamColors.primary === 'bg-indigo-600') {
      return {
        bar: 'from-indigo-500 to-violet-500',
        text: 'from-indigo-600 to-violet-600',
      };
    } else if (teamColors.primary === 'bg-teal-600') {
      return {
        bar: 'from-teal-500 to-cyan-500',
        text: 'from-teal-600 to-cyan-600',
      };
    } else if (teamColors.primary === 'bg-emerald-600') {
      return {
        bar: 'from-emerald-500 to-teal-500',
        text: 'from-emerald-600 to-teal-600',
      };
    } else if (teamColors.primary === 'bg-blue-600') {
      return {
        bar: 'from-blue-500 to-cyan-500',
        text: 'from-blue-600 to-cyan-600',
      };
    } else {
      return {
        bar: 'from-orange-500 to-amber-500',
        text: 'from-orange-600 to-amber-600',
      };
    }
  };

  const gradients = getGradientClasses();

  // Get hover class for button
  const getButtonHoverClass = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'hover:bg-indigo-700';
    if (teamColors.primary === 'bg-teal-600') return 'hover:bg-teal-700';
    if (teamColors.primary === 'bg-emerald-600') return 'hover:bg-emerald-700';
    if (teamColors.primary === 'bg-blue-600') return 'hover:bg-blue-700';
    return 'hover:bg-orange-700';
  };

  // Get watermark color
  const getWatermarkColor = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'text-indigo-900';
    if (teamColors.primary === 'bg-teal-600') return 'text-teal-900';
    if (teamColors.primary === 'bg-emerald-600') return 'text-emerald-900';
    if (teamColors.primary === 'bg-blue-600') return 'text-blue-900';
    return 'text-orange-900';
  };

  // Get shadow color
  const getShadowColor = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'shadow-indigo-500/20';
    if (teamColors.primary === 'bg-teal-600') return 'shadow-teal-500/20';
    if (teamColors.primary === 'bg-emerald-600') return 'shadow-emerald-500/20';
    if (teamColors.primary === 'bg-blue-600') return 'shadow-blue-500/20';
    return 'shadow-orange-500/20';
  };

  return (
    <div className="fixed inset-0 z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"></div>

      {/* Modal Content */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className={`relative transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl ${getShadowColor()} transition-all w-full max-w-3xl ring-1 ring-slate-200 animate__animated animate__zoomIn animate__faster`}>

            {/* Watermark */}
            <div className="absolute -bottom-24 -right-24 pointer-events-none select-none opacity-5 transform -rotate-12 z-0">
              <svg className={`w-[500px] h-[500px] ${getWatermarkColor()}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>

            <div className="p-10 sm:p-14 flex flex-col items-start relative z-10">
              {/* Header */}
              <div className={`w-16 h-2 bg-gradient-to-r ${gradients.bar} rounded-full mb-8`}></div>
              <h3 className="text-4xl sm:text-6xl font-black text-slate-900 mb-8 leading-none tracking-tight">
                <span className={`text-transparent bg-clip-text bg-gradient-to-r ${gradients.text}`}>
                  {t('game.scenario_title')}
                </span>
              </h3>

              {/* Content */}
              <div className={`space-y-6 text-lg text-slate-600 leading-relaxed font-medium mb-10 border-l-4 ${teamColors.primaryBorderLight} pl-6`}>
                {gameMode === 'leadlab' ? (
                  <>
                    <p>{t('game.scenario_leadlab_p1')}</p>
                    <p>{t('game.scenario_leadlab_p2')}</p>
                    <p>{t('game.scenario_leadlab_p3')}</p>
                  </>
                ) : (
                  <>
                    <p>{t('game.scenario_p1')}</p>
                    <p>{t('game.scenario_p2')}</p>
                    <p>{t('game.scenario_p3')}</p>
                    <p>{t('game.scenario_p4')}</p>
                    <p>{t('game.scenario_p5_teamlab')}</p>
                  </>
                )}
              </div>

              {/* Action Button */}
              <div className="w-full sm:w-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className={`inline-flex justify-center items-center gap-2 rounded-2xl ${teamColors.primary} px-5 py-2.5 text-base font-bold text-white shadow-lg ${teamColors.shadow} transition-transform transform hover:scale-105 active:scale-95 ${getButtonHoverClass()}`}
                >
                  <span>{t('game.close', 'C\'EST PARTI !')}</span>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
